import { useState, useEffect, useCallback } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import axios from 'axios'
import { API_CONFIG } from '../../config/apiConfig'
import './index.css'
import {showToast} from "../../utils/messageUtil";
import { useUser } from '../../contexts/UserContext';
import { handleJSAPIAccess } from '../../utils/auth_access_util';
import { parseLocalDateTime } from '../../utils/dateUtil';
import TimeRangePicker from '../time-select/TimeRangePicker';
import LocationSearchInput from '../../components/LocationSearchInput';

export default function Dispatch() {
    const navigate = useNavigate()
    const location = useLocation()
    const { userInfo, loading: userLoading, refreshUserInfo, isAdmin, isDirectDispatch, checkingAdmin, checkingDirectDispatch } = useUser()

    // 权限守卫：管理员 或 有直接派车权限的用户可访问，否则提示并返回首页
    useEffect(() => {
        if (userLoading || checkingAdmin || checkingDirectDispatch) return
        if (!isAdmin && !isDirectDispatch) {
            showToast('您没有直接派车权限', 'error')
            navigate('/', { replace: true })
        }
    }, [userLoading, isAdmin, isDirectDispatch, checkingAdmin, checkingDirectDispatch])

    // 检查用户信息是否完整
    const isUserInfoComplete = () => {
        if (!userInfo) return false
        const hasUserId = userInfo.open_id || userInfo.user_id
        const hasName = userInfo.name && userInfo.name.trim() !== ''
        const hasMobile = userInfo.mobile && userInfo.mobile.trim() !== ''
        const hasDepartment = userInfo.department || userInfo.department_name
        return hasUserId && hasName && hasMobile && hasDepartment
    }

    const [hasRefreshed, setHasRefreshed] = useState(false)

    // 如果用户信息缺失，重新获取
    useEffect(() => {
        // 只有在加载完成后、用户信息不完整且还未刷新过时才重新获取
        if (!userLoading && !isUserInfoComplete() && !hasRefreshed) {
            console.log('派车页面：用户信息不完整，重新获取...')
            setHasRefreshed(true)
            refreshUserInfo()
        }
    }, [userLoading, userInfo, refreshUserInfo, hasRefreshed])

    // 格式化本地时间为ISO格式
    const formatLocalTime = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:00`;
    };

    const getInitialFormData = () => {
        const savedData = localStorage.getItem('dispatchFormData');
        if (savedData) {
            try {
                const parsedData = JSON.parse(savedData);
                // 从地图选择页面返回时保留已有数据（含开始/结束时间），不清空也不重置
                // location.state 在浏览器返回等场景可能丢失，用 sessionStorage 标记兜底
                const isFromMapSelect = location.state?.from === 'map-select' ||
                    sessionStorage.getItem('dispatchFromMap') === '1';
                if (!isFromMapSelect) {
                    // 仅新进入页面（非地图选点返回）时：重置开始/结束时间为当前时间+最小用车时长，并清空用车人、同行人、车牌号、出发地、目的地、用车原因
                    const now = new Date();
                    parsedData.expectedStartTime = formatLocalTime(now);
                    const minMinutes = API_CONFIG.minApplicationMinutes || 60;
                    const endTime = new Date(now.getTime() + minMinutes * 60 * 1000);
                    parsedData.expectedEndTime = formatLocalTime(endTime);
                    parsedData.vehicleUserName = '';
                    parsedData.vehicleUserCode = '';
                    parsedData.vehicleUserPhone = '';
                    parsedData.passengers = '';
                    parsedData.passengersCode = '';
                    parsedData.plateNumber = '';
                    parsedData.expectedLocation = '';
                    parsedData.destination = '';
                    parsedData.reason = '';
                    parsedData.selectedCity = '';
                    parsedData.applicantDept = '';
                }
                return parsedData;
            } catch (error) {
                console.error('解析表单数据失败:', error);
            }
        }
        // 设置开始和结束时间为当前本地时间
        const now = new Date();
        const startTime = formatLocalTime(now);
        // 结束时间默认比开始时间晚最小用车时长（租户配置，默认 60 分钟）
        const minMinutes = API_CONFIG.minApplicationMinutes || 60;
        const endTime = new Date(now.getTime() + minMinutes * 60 * 1000);
        const endTimeStr = formatLocalTime(endTime);
        return {
            applicantName: '',
            applicantPhone: '',
            applyTime: new Date().toISOString().split('T')[0],
            applicantDept: '',
            employeeId: '',
            applicantOpenId: '',
            applicantId: '',
            plateNumber: '',
            driveType: '1',
            // 开始时间默认为当前本地时间，结束时间默认为当前本地时间+1小时
            expectedStartTime: startTime,
            expectedEndTime: endTimeStr,
            expectedLocation: '',
            destination: '',
            vehicleUserName: '', // 用车人姓名(逗号分隔)
            vehicleUserCode: '', // 用车人openId(逗号分隔)
            passengers: '',
            passengersCode: '',
            reason: ''
        };
    };
    const [formData, setFormData] = useState(getInitialFormData());
    const [submitting, setSubmitting] = useState(false)
    const [availableVehicles, setAvailableVehicles] = useState([])
    const [loadingVehicles, setLoadingVehicles] = useState(false)
    const [cityList, setCityList] = useState([])
    const [selectedCity, setSelectedCity] = useState('')
    // 时间范围选择器弹窗（PC端点击时间输入框弹出，与时间选择页一致）
    const [showTimeRangePicker, setShowTimeRangePicker] = useState(false)

    // 手机端（≤768px）检测：用车时间框改用「月日时分」短格式，减少宽度防溢出
    const [isMobileWidth, setIsMobileWidth] = useState(
        typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
    )
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 768px)')
        const onMqChange = (e) => setIsMobileWidth(e.matches)
        if (mq.addEventListener) mq.addEventListener('change', onMqChange)
        else mq.addListener(onMqChange)
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', onMqChange)
            else mq.removeListener(onMqChange)
        }
    }, [])

    // 用车时间框显示格式：手机端月日时分（MM-DD HH:mm），桌面端年月日时分
    const formatBoxDateTime = (dateTimeStr) => {
        if (!dateTimeStr) return '';
        const s = dateTimeStr.slice(0, 16).replace('T', ' ');
        return isMobileWidth ? s.slice(5) : s;
    };

    // 打开时间范围选择器
    const openTimeRangePicker = () => setShowTimeRangePicker(true);

    // "YYYY-MM-DDTHH:mm:00" → Date（用于 TimeRangePicker 初始值）
    const parseFormTime = (str) => {
        if (!str) return new Date();
        const d = new Date(str);
        return isNaN(d.getTime()) ? new Date() : d;
    };

    // TimeRangePicker 确认：同时更新开始/结束时间
    const handleTimeRangeConfirm = (startDateTime, endDateTime, allDay) => {
        setShowTimeRangePicker(false);
        // 校验用车时长不能低于最小用车时长（租户配置，默认 60 分钟）
        // 不足时：提示 + 开始时间不变，结束时间自动延长到 开始时间+最小时长
        if (startDateTime && endDateTime) {
            const minMinutes = API_CONFIG.minApplicationMinutes || 60;
            const durationMinutes = (endDateTime.getTime() - startDateTime.getTime()) / 60000;
            if (durationMinutes < minMinutes) {
                endDateTime = new Date(startDateTime.getTime() + minMinutes * 60000);
                showToast(`预约时长不能低于${minMinutes}分钟，已自动延长结束时间`, 'warning');
            }
        }
        const toLocalInput = (d) => {
            if (!d || isNaN(d.getTime())) return '';
            const pad = n => String(n).padStart(2, '0');
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
        };
        setFormData(prev => ({
            ...prev,
            expectedStartTime: toLocalInput(startDateTime),
            expectedEndTime: toLocalInput(endDateTime)
        }));
    };

    // 当用户信息加载完成后，更新表单数据
    useEffect(() => {
        if (userInfo) {
            // console.log('用户信息已加载1:', userInfo);
            // console.log('部门信息1:', userInfo.department_name);
            // console.log('部门路径1:', userInfo.department_path);
            setFormData(prev => {
                const newDept = userInfo.department_name || prev.applicantDept;
                // 从用户信息中自动获取手机号，去掉+86前缀（优先用飞书用户信息更新，不依赖 localStorage）
                const userPhone = (userInfo.mobile || '').replace(/^\+86/, '');
                console.log('[Dispatch] userInfo.mobile=%s, userPhone=%s', userInfo.mobile, userPhone);
                console.log('准备更新部门信息为1:', newDept);
                return {
                    ...prev,
                    applicantName: userInfo.name || prev.applicantName,
                    applicantPhone: userPhone || prev.applicantPhone,
                    employeeId: userInfo.user_id || prev.employeeId,
                    applicantOpenId: userInfo.open_id || prev.applicantOpenId,
                    applicantId: userInfo.open_id || prev.applicantId,
                    applicantDept: newDept,
                };
            });
        }
    }, [userInfo]);
    // 获取可预约的车辆
    const fetchAvailableVehicles = useCallback(async () => {
        try {
            const { expectedStartTime, expectedEndTime } = formData

            if (!expectedStartTime || !expectedEndTime) {
                setAvailableVehicles([])
                return
            }

            setLoadingVehicles(true)
            const response = await axios.post(`${API_CONFIG.apiBaseUrl}/carApplication/getVehicleTime`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined,
                startTime: expectedStartTime.replace('T', ' '),
                endTime: expectedEndTime.replace('T', ' ')
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })

            if (response.data && response.data.code === 200) {
                setAvailableVehicles(response.data.data || [])
            } else {
                setAvailableVehicles([])
            }
        } catch (error) {
            console.error('获取可预约车辆失败:', error)
            setAvailableVehicles([])
        } finally {
            setLoadingVehicles(false)
        }
    }, [formData.expectedStartTime, formData.expectedEndTime])


    // 监听formData变化，保存到localStorage
    useEffect(() => {
        localStorage.setItem('dispatchFormData', JSON.stringify(formData));
    }, [formData]);
    // 监听时间变化，获取可预约车辆
    useEffect(() => {
        if (formData.expectedStartTime && formData.expectedEndTime) {
            fetchAvailableVehicles()
        }
    }, [formData.expectedStartTime, formData.expectedEndTime, fetchAvailableVehicles])

    // 提取城市列表
    useEffect(() => {
        if (availableVehicles?.length) {
            const cities = [...new Set(availableVehicles.map(i => i.cityName).filter(Boolean))]
            setCityList(cities)
        }
    }, [availableVehicles])

    // 从sessionStorage获取位置信息
    useEffect(() => {
        // 延迟执行，确保sessionStorage中的数据已被正确设置
        const timer = setTimeout(() => {
            // 检查是否有新的位置信息格式（包含出发地和目的地）
            const selectedLocations = sessionStorage.getItem('selectedLocations');
            if (selectedLocations) {
                try {
                    const locationsData = JSON.parse(selectedLocations);
                    // console.log('获取到选择的出发地和目的地信息:', locationsData);

                    // 同时更新出发地和目的地
                    setFormData(prev => ({
                        ...prev,
                        expectedLocation: locationsData.from.name,
                        destination: locationsData.to.name
                    }));

                    // 清除sessionStorage中的数据，避免重复赋值
                    sessionStorage.removeItem('selectedLocations');
                    console.log('位置信息赋值完成，已清除sessionStorage中的数据');
                    return;
                } catch (error) {
                    console.error('解析位置信息失败:', error);
                    sessionStorage.removeItem('selectedLocations');
                }
            }

            // 兼容旧的位置信息格式
            const selectedLocation = sessionStorage.getItem('selectedLocation');
            if (selectedLocation) {
                try {
                    const locationData = JSON.parse(selectedLocation);
                    // console.log('获取到选择的位置信息:', locationData);

                    // 根据type赋值给对应的输入框，只更新对应的字段
                    if (locationData.type === 'from') {
                        // 赋值给出发地，保持目的地不变
                        setFormData(prev => ({
                            ...prev,
                            expectedLocation: locationData.location.name
                        }));
                    } else if (locationData.type === 'to') {
                        // 赋值给目的地，保持出发地不变
                        setFormData(prev => ({
                            ...prev,
                            destination: locationData.location.name
                        }));
                    }

                    // 清除sessionStorage中的数据，避免重复赋值
                    sessionStorage.removeItem('selectedLocation');
                    console.log('位置信息赋值完成，已清除sessionStorage中的数据');
                } catch (error) {
                    console.error('解析位置信息失败:', error);
                    sessionStorage.removeItem('selectedLocation');
                }
            }

            // 检查是否有单独存储的出发地和目的地信息
            const fromLocation = sessionStorage.getItem('fromLocation');
            const toLocation = sessionStorage.getItem('toLocation');
            if (fromLocation || toLocation) {
                // console.log('获取到单独存储的位置信息:', { fromLocation, toLocation });

                // 更新表单数据
                setFormData(prev => ({
                    ...prev,
                    expectedLocation: fromLocation || prev.expectedLocation,
                    destination: toLocation || prev.destination
                }));

                // 清除sessionStorage中的数据，避免重复赋值
                sessionStorage.removeItem('fromLocation');
                sessionStorage.removeItem('toLocation');
                console.log('单独存储的位置信息赋值完成，已清除sessionStorage中的数据');
            }
        }, 100);

        return () => clearTimeout(timer);
    }, [location])

    const handleChange = (e) => {
        const { name, value } = e.target
        // 确保时间字段包含秒数
        let processedValue = value
        if (name === 'expectedStartTime' || name === 'expectedEndTime') {
            if (value.length === 16) {
                // 将value格式化为YYYY-MM-DDTHH:mm:ss
                processedValue = value + ':00'
            }
        }

        // 更新表单数据
        setFormData(prev => {
            let newFormData = {
                ...prev,
                [name]: processedValue
            }

            // 空值保护：datetime-local 点击"清除"按钮后 value 是空字符串，
            // parseLocalDateTime('') 返回 null，再 .getTime() 会抛 TypeError 导致整个组件崩溃
            // 这里跳过自动调整逻辑，只更新 formData
            const startStr = newFormData.expectedStartTime
            const endStr = newFormData.expectedEndTime
            if (!startStr || !endStr) {
                return newFormData
            }

            // 当修改开始时间时，结束时间自动+最小用车时长
            if (name === 'expectedStartTime') {
                const startTime = parseLocalDateTime(startStr)
                const endTime = parseLocalDateTime(endStr)
                if (!startTime || !endTime) {
                    return newFormData
                }
                // 计算时间差（分钟）
                const durationMinutes = (endTime.getTime() - startTime.getTime()) / (1000 * 60)
                // 租户配置的最小用车时长（默认 60 分钟）
                const minMinutes = API_CONFIG.minApplicationMinutes || 60

                // 如果结束时间早于开始时间或时长小于最小用车时长，自动调整
                if (endTime <= startTime || durationMinutes < minMinutes) {
                    // 自动调整结束时间为开始时间之后最小用车时长
                    const adjustedEndTime = new Date(startTime.getTime() + minMinutes * 60 * 1000)
                    newFormData.expectedEndTime = formatLocalTime(adjustedEndTime)
                    showToast(`结束时间已自动调整为开始时间之后${minMinutes}分钟`, 'info')
                }
            } else if (name === 'expectedEndTime') {
                // 当修改结束时间时，校验时长不能低于最小用车时长
                const startTime = parseLocalDateTime(startStr)
                const endTime = parseLocalDateTime(endStr)
                if (!startTime || !endTime) {
                    return newFormData
                }
                // 租户配置的最小用车时长（默认 60 分钟）
                const minMinutes = API_CONFIG.minApplicationMinutes || 60

                // 计算时间差（分钟）
                const durationMinutes = (endTime.getTime() - startTime.getTime()) / (1000 * 60)

                // 如果结束时间早于开始时间或时长小于最小用车时长，不更新并提示
                if (endTime <= startTime || durationMinutes < minMinutes) {
                    // 恢复原来的值
                    newFormData.expectedEndTime = prev.expectedEndTime
                    showToast(`预约时长不能低于${minMinutes}分钟`, 'error')
                }
            }

            return newFormData
        })
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        // 防止重复提交
        if (submitting) {
            return
        }

        // 检查必填项
        if (!formData.vehicleUserName || !formData.vehicleUserCode || !formData.reason || !formData.expectedStartTime || !formData.expectedEndTime) {
            showToast('请填写必填项', 'error')
            return
        }
        
        // 检查申请人部门是否为空
        if (!formData.applicantDept || formData.applicantDept.trim() === '') {
            showToast('申请人部门信息获取失败，请刷新页面重试', 'error')
            return
        }

        // 检查结束时间是否大于开始时间
        const startTime = parseLocalDateTime(formData.expectedStartTime)
        const endTime = parseLocalDateTime(formData.expectedEndTime)
        if (endTime <= startTime) {
            showToast('结束时间必须大于开始时间', 'error')
            return
        }
        
        // 校验时长不能低于最小用车时长（租户配置，默认 60 分钟）
        const minMinutes = API_CONFIG.minApplicationMinutes || 60
        const durationMinutes = (endTime.getTime() - startTime.getTime()) / (1000 * 60)
        if (durationMinutes < minMinutes) {
            showToast(`预约时长不能低于${minMinutes}分钟`, 'error')
            return
        }

        try {
            setSubmitting(true)
            // 构建请求参数，与预约页面保持一致
            const requestData = {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined,
                plateNumber: formData.plateNumber,
                applicantId: formData.applicantId,
                applicantOpenId: formData.applicantOpenId,
                applicantName: formData.applicantName,
                applicantPhone: formData.applicantPhone,
                applicantDept: formData.applicantDept,
                applyTime: new Date().toISOString().replace('T', ' ').substring(0, 19),
                reason: formData.reason,
                expectedStartTime: formData.expectedStartTime.replace('T', ' '),
                expectedEndTime: formData.expectedEndTime.replace('T', ' '),
                expectedLocation: formData.expectedLocation,
                destination: formData.destination,
                driveType: 1, // 默认自驾
                applicationType: 0, // 公车申请：0-公车，1-私车
                // 用车人是当前用户本人 → isPersonal=0（本人申请）；否则 1（代人申请）
                isPersonal: (formData.vehicleUserCode || '').trim() === (formData.applicantOpenId || '').trim() ? 0 : 1,
                vehicleUserName: formData.vehicleUserName,
                vehicleUserCode: formData.vehicleUserCode,
                passengers: formData.passengers,
                passengersCode: formData.passengersCode
            }

            console.log('提交派车信息:', requestData)

            // 调用与预约页面相同的后端API
            const response = await axios.post(`${API_CONFIG.apiBaseUrl}/carApplication/dispatch`, requestData, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })

            console.log('API响应:', response.data)

            // 处理响应
            if (response.data && response.data.code === 200) {
                showToast('派车成功！', 'success')
                // 清除localStorage中的表单数据
                localStorage.removeItem('dispatchFormData');
                navigate('/')
            } else {
                showToast(`派车失败: ${response.data.message || '未知错误'}`, 'error')
            }
        } catch (error) {
            console.error('提交派车失败:', error)
            showToast('网络错误，请稍后重试', 'error')
        } finally {
            setSubmitting(false)
        }
    }

    const handleCancel = () => {
        navigate('/')
    }

    // 出发地/目的地已改为输入框内联地图检索（LocationSearchInput），不再跳转地图选择页面

    // 组件挂载后清除"从地图返回"标记，避免下次进入误判
    useEffect(() => {
        sessionStorage.removeItem('dispatchFromMap');
    }, []);

    // 处理联系人选择结果
    const handleContactSelectResult = (res) => {
        console.log('处理联系人选择结果:', res);

        // 处理选择的用户
        if (res.data && res.data.length > 0) {
            const selectedUsers = res.data;
            console.log('选择的用户:', selectedUsers);

            // 过滤掉已选择的用车人
            const vehicleUserIds = formData.vehicleUserCode ? formData.vehicleUserCode.split(',').filter(id => id.trim()) : [];
            let filteredByDriver = 0;

            const filteredUsers = selectedUsers.filter(user => {
                const userOpenId = user.open_id || user.openId || user.user_id;
                // 排除已选择的用车人
                if (vehicleUserIds.includes(userOpenId)) {
                    filteredByDriver++;
                    return false;
                }
                return true;
            });

            // 如果没有有效用户，提示错误
            if (filteredUsers.length === 0) {
                if (filteredByDriver > 0) {
                    showToast('不能选择已选择的用车人作为同行人，请重新选择', 'error');
                } else {
                    showToast('请重新选择同行人', 'error');
                }
                return;
            }

            // 如果过滤掉了部分用户，提示用户
            if (filteredUsers.length < selectedUsers.length) {
                showToast('已排除已选择的用车人，不能选择作为同行人', 'error');
            }

            // 提取姓名和openId,分别用逗号分隔
            const names = filteredUsers.map(user => user.name).filter(name => name);
            const openIds = filteredUsers.map(user => user.openId).filter(openId => openId);

            console.log('提取的姓名:', names);
            console.log('提取的openId:', openIds);

            // 更新表单数据
            setFormData(prev => ({
                ...prev,
                passengers: names.join(','), // 逗号分隔的姓名
                passengersCode: openIds.join(',') // 逗号分隔的openId
            }));

            showToast(`已选择 ${names.length} 位联系人`, 'success');
        } else {
            console.warn('未选择联系人');
            showToast('未选择联系人', 'error');
        }
    };

    // 删除单个同行人（同步删除姓名和openId）
    const removePassenger = (index) => {
        setFormData(prev => {
            const names = (prev.passengers || '').split(',').filter(n => n.trim());
            const codes = (prev.passengersCode || '').split(',').filter(c => c.trim());
            if (index < 0 || index >= names.length) return prev;
            names.splice(index, 1);
            codes.splice(index, 1);
            return {
                ...prev,
                passengers: names.join(','),
                passengersCode: codes.join(',')
            };
        });
    };

    // 打开飞书联系人选择器
    const openLarkContactPicker = async () => {
        console.log('openLarkContactPicker 被调用');
        console.log('window.h5sdk:', window.h5sdk);
        console.log('window.tt:', window.tt);

        // 确保JSAPI鉴权完成
        const isJSAPIAuthorized = await new Promise((resolve) => {
            if (window.h5sdk && window.h5sdk.config) {
                // 重新进行JSAPI鉴权
                handleJSAPIAccess((isSuccess) => {
                    resolve(isSuccess);
                });
            } else {
                resolve(false);
            }
        });

        if (!isJSAPIAuthorized) {
            showToast('飞书JSAPI鉴权失败，无法打开联系人选择', 'error');
            return;
        }

        const tt = window.h5sdk.tt || window.tt;
        console.log('tt对象:', tt);
        console.log('tt.chooseContact:', tt?.chooseContact);

        if (!tt || !tt.chooseContact) {
            showToast('当前飞书版本不支持联系人选择', 'error');
            return;
        }

        console.log('开始调用 tt.chooseContact');
        tt.chooseContact({
            multi: true,
            ignore: false,
            maxNum: 6,
            limitTips: "最多只能选择6个同行人",
            externalContact: false,
            enableChooseDepartment: false,
            success: (res) => {
                handleContactSelectResult(res);
            },
            fail: (res) => {
                // 用户取消选择或关闭选择器，不报错
                console.log('取消选择同行人:', res);
            }
        });
    };

    // 打开用车人选择器
    const openDriverPicker = async () => {
        console.log('openDriverPicker 被调用');
        console.log('window.h5sdk:', window.h5sdk);
        console.log('window.tt:', window.tt);

        // 确保JSAPI鉴权完成
        const isJSAPIAuthorized = await new Promise((resolve) => {
            if (window.h5sdk && window.h5sdk.config) {
                // 重新进行JSAPI鉴权
                handleJSAPIAccess((isSuccess) => {
                    resolve(isSuccess);
                });
            } else {
                resolve(false);
            }
        });

        if (!isJSAPIAuthorized) {
            showToast('飞书JSAPI鉴权失败，无法打开联系人选择', 'error');
            return;
        }

        const tt = window.h5sdk.tt || window.tt;
        console.log('tt对象:', tt);
        console.log('tt.chooseContact:', tt?.chooseContact);

        if (!tt || !tt.chooseContact) {
            showToast('当前飞书版本不支持联系人选择', 'error');
            return;
        }

        console.log('开始调用 tt.chooseContact 选择用车人');
        tt.chooseContact({
            multi: false, // 用车人只能选一个
            ignore: false,
            maxNum: 1,
            limitTips: "只能选择1个用车人",
            externalContact: false,
            enableChooseDepartment: false,
            success: (res) => {
                handleDriverSelectResult(res);
            },
            fail: (res) => {
                // 用户取消选择或关闭选择器，不报错
                console.log('取消选择用车人:', res);
            }
        });
    };

    // 处理用车人选择结果
    const handleDriverSelectResult = (res) => {
        console.log('处理用车人选择结果:', res);

        if (res.data && res.data.length > 0) {
            const selectedUsers = res.data;
            console.log('选择的用车人:', selectedUsers);

            // 过滤掉已选择的同行人
            const passengerIds = formData.passengersCode ? formData.passengersCode.split(',').filter(id => id.trim()) : [];
            let filteredByPassenger = 0;
            
            const filteredUsers = selectedUsers.filter(user => {
                const userOpenId = user.open_id || user.openId || user.user_id;
                // 排除已选择的同行人
                if (passengerIds.includes(userOpenId)) {
                    filteredByPassenger++;
                    return false;
                }
                return true;
            });

            // 如果没有有效用户，提示错误
            if (filteredUsers.length === 0) {
                if (filteredByPassenger > 0) {
                    showToast('不能选择已选择的同行人作为用车人，请重新选择', 'error');
                } else {
                    showToast('请重新选择用车人', 'error');
                }
                return;
            }

            // 如果过滤掉了部分用户，提示用户
            if (filteredUsers.length < selectedUsers.length) {
                showToast('已排除已选择的同行人，不能选择作为用车人', 'info');
            }

            // 提取姓名、openId和电话
            const user = filteredUsers[0];
            const name = user.name || '';
            const userOpenId = user.open_id || user.openId || user.user_id || '';
            const phone = (user.mobile || '').replace(/^\+86/, '') || '';

            console.log('提取的姓名:', name);
            console.log('提取的openId:', userOpenId);
            console.log('提取的电话:', phone);

            // 更新表单数据
            setFormData(prev => ({
                ...prev,
                vehicleUserName: name,
                vehicleUserCode: userOpenId,
                vehicleUserPhone: phone
            }));

            showToast(`已选择用车人: ${name}`, 'success');
        } else {
            console.warn('未选择用车人');
            showToast('未选择用车人', 'info');
        }
    };

    return (
        <div className="dispatch-page">
            <h2>新增派车</h2>
            {userLoading && (
            <div className="loading-indicator">
                <p>正在获取用户信息...</p>
            </div>
        )}
            <form onSubmit={handleSubmit} className="dispatch-form">
                <div className="form-group">
                    <label>用车人:<span className="required">*</span></label>
                    <div className="contact-input-wrapper">
                        <input
                            type="text"
                            name="vehicleUserName"
                            value={formData.vehicleUserName}
                            onChange={handleChange}
                            required
                            readOnly
                            placeholder="请选择用车人（最多1个）"
                            onClick={openDriverPicker}
                            title="点击选择用车人"
                        />
                        <button type="button" className="contact-select-btn" onClick={openDriverPicker} title="选择用车人">
                            👤
                        </button>
                    </div>
                </div>
                <div className="form-group">
                    <label>用车时间:<span className="required">*</span></label>
                    {/* 合并为单个时间框（开始 → 结束），点击弹出 TimeRangePicker */}
                    <div className="datetime-box-container">
                        <div className="datetime-box datetime-box-merged" onClick={openTimeRangePicker} title="点击选择用车时间">
                            <span className="datetime-box-label">开始</span>
                            <span className="datetime-box-value">
                                {formData.expectedStartTime
                                    ? formatBoxDateTime(formData.expectedStartTime)
                                    : '请选择'}
                            </span>
                            <span className="datetime-box-arrow">→</span>
                            <span className="datetime-box-label">结束</span>
                            <span className="datetime-box-value">
                                {formData.expectedEndTime
                                    ? formatBoxDateTime(formData.expectedEndTime)
                                    : '请选择'}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="form-group">
                    <label>车辆归属地筛选:</label>
                    <select className="city-filter" value={selectedCity} onChange={e=>setSelectedCity(e.target.value)}>
                        <option value="">全部城市</option>
                        {cityList.map(c=> <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>
                <div className="form-group">
                    <label>车牌号:<span className="required">*</span></label>
                    <select
                        name="plateNumber"
                        value={formData.plateNumber}
                        onChange={handleChange}
                        required
                    >
                        <option value="">选择时间后显示</option>
                        {availableVehicles.filter(i => !selectedCity || i.cityName == selectedCity).map((vehicle, index) => (
                            <option key={index} value={vehicle.plateNumber}>
                                {vehicle.plateNumber} {vehicle.modelName} {vehicle.cityName ? `[${vehicle.cityName}]` : ''}
                            </option>
                        ))}
                    </select>
                    {loadingVehicles && <div className="loading-text">正在加载车辆...</div>}
                </div>
                <div className="form-group">
                    <label>出发地:<span className="required">*</span></label>
                    <div className="contact-input-wrapper">
                        <LocationSearchInput
                            name="expectedLocation"
                            value={formData.expectedLocation}
                            onChange={(val) => handleChange({ target: { name: 'expectedLocation', value: val } })}
                            required
                            placeholder="请输入出发地"
                        />
                    </div>
                </div>
                <div className="form-group">
                    <label>目的地:<span className="required">*</span></label>
                    <div className="contact-input-wrapper">
                        <LocationSearchInput
                            name="destination"
                            value={formData.destination}
                            onChange={(val) => handleChange({ target: { name: 'destination', value: val } })}
                            required
                            placeholder="请输入目的地"
                        />
                    </div>
                </div>
                <div className="form-group">
                    <label>同行人:</label>
                    <div className="contact-input-wrapper">
                        <input
                            type="text"
                            name="passengers"
                            value={formData.passengers}
                            onChange={handleChange}
                            readOnly
                            placeholder="请选择同行人（最多6个）"
                            onClick={openLarkContactPicker}
                            title="点击选择同行人"
                        />
                        <button type="button" className="contact-select-btn" onClick={openLarkContactPicker} title="选择同行人">
                            👥
                        </button>
                    </div>
                    {/* 已选同行人标签（可单独删除） */}
                    {formData.passengers && (
                        <div className="dispatch-passenger-tags">
                            {formData.passengers.split(',').filter(n => n.trim()).map((name, index) => (
                                <span key={index} className="dispatch-passenger-tag">
                                    {name}
                                    <button
                                        type="button"
                                        className="dispatch-passenger-remove"
                                        onClick={() => removePassenger(index)}
                                        title="删除该同行人"
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                </div>
                <div className="form-group">
                    <label>用车原因:<span className="required">*</span></label>
                    <textarea
                        name="reason"
                        value={formData.reason}
                        onChange={handleChange}
                        required
                        placeholder="请输入用车原因"
                    ></textarea>
                </div>
                <div className="form-actions">
                    <button type="submit" className="submit-btn" disabled={submitting}>
                        {submitting ? '提交中...' : '确定'}
                    </button>
                    <button type="button" className="cancel-btn" onClick={handleCancel}>
                        取消
                    </button>
                </div>
            </form>
            {/* 时间范围选择器弹窗（点击开始/结束时间输入框弹出） */}
            <TimeRangePicker
                open={showTimeRangePicker}
                onClose={() => setShowTimeRangePicker(false)}
                initialStartTime={parseFormTime(formData.expectedStartTime)}
                initialEndTime={parseFormTime(formData.expectedEndTime)}
                onConfirm={handleTimeRangeConfirm}
            />
        </div>
    )
}